import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

const formFieldLabelSx = {
  fontSize: "0.875rem",
  fontWeight: 500,
  color: "#262626",
  mb: 0.75,
} as const;

export type AssessmentNameOption = Readonly<{
  id: string;
  name: string;
}>;

type AssessmentNameFieldProps = Readonly<{
  value: string;
  onChange: (value: string) => void;
  /** Dropdown options from GET `/plans/names`. */
  options?: readonly AssessmentNameOption[];
  loading?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  /** Fired when the options popup opens — parent should fetch fresh names. */
  onOpen?: () => void;
  /**
   * When false, the user can only choose a listed name.
   * Classification, Conditional Classification, and Object to Object
   * keep free-text entry plus the saved-name dropdown.
   */
  allowFreeText?: boolean;
}>;

/**
 * Assessment Name. Keep the label, aria-label, and validation copy aligned
 * as "Assessment Name".
 */
export default function AssessmentNameField({
  value,
  onChange,
  options = [],
  loading = false,
  disabled = false,
  error = false,
  helperText,
  onOpen,
  allowFreeText = true,
}: AssessmentNameFieldProps) {
  const selected =
    options.find((option) => option.name === value) ??
    (value.trim() ? { id: value, name: value } : null);
  const dropdownOptions =
    selected && !options.some((option) => option.name === selected.name)
      ? [selected, ...options]
      : [...options];

  return (
    <Box>
      <Typography sx={formFieldLabelSx}>
        Assessment Name
        <Box component="span" sx={{ color: "error.main", ml: 0.25 }}>
          *
        </Box>
      </Typography>
      <Autocomplete
        freeSolo={allowFreeText}
        forcePopupIcon
        size="small"
        fullWidth
        options={dropdownOptions}
        loading={loading}
        disabled={disabled}
        value={selected}
        inputValue={value}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.name
        }
        isOptionEqualToValue={(option, selectedValue) =>
          option.id === selectedValue.id || option.name === selectedValue.name
        }
        onOpen={() => {
          onOpen?.();
        }}
        onChange={(_event, next) => {
          if (typeof next === "string") {
            onChange(allowFreeText ? next : "");
            return;
          }
          onChange(next?.name ?? "");
        }}
        onInputChange={(_event, next, reason) => {
          // Only propagate real typing/clear — ignore reset/blur/highlight so
          // sibling Autocompletes on Add One Item rows do not cross-update.
          if (reason !== "input" && reason !== "clear") return;
          if (!allowFreeText) {
            if (reason === "clear") onChange("");
            return;
          }
          onChange(next);
        }}
        noOptionsText={loading ? "Loading…" : "No saved names yet"}
        renderInput={(params) => (
          <TextField
            {...params}
            error={error}
            helperText={helperText}
            placeholder={
              allowFreeText
                ? "Enter or select assessment name"
                : "Select assessment name"
            }
            inputProps={{
              ...params.inputProps,
              "aria-label": "Assessment Name",
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px",
                bgcolor: "#fff",
              },
            }}
          />
        )}
      />
    </Box>
  );
}
